/*
 * Project Page Template Component
 *
 */

import React from "react"
import { useStoryblokState, storyblokEditable } from "gatsby-source-storyblok"
import Base from "../base/base"
import Button from "../../atoms/button/button"
import Link from "../../atoms/link/link"
import HeroImage from "../../molecules/heroImage/heroImage"
import RichText from "../../molecules/richText/richText"
import Figure from "../../molecules/figure/figure"
import Seo from "../../atoms/seo/seo"

const ProjectPage = ({ pageContext, location }) => {
  let story = pageContext.story
  story = useStoryblokState(story, location)

  const getDate = dateData => {
    const dateObj = new Date(dateData)
    const month = dateObj.toLocaleString("en-GB", { month: "long" })
    const year = dateObj.getFullYear()
    const date = `${month} ${year}`

    return date
  }

  return (
    <>
      <Base>
        <div
          className="grid__col-1-6 grid default-template project-page-page"
          {...storyblokEditable(story)}
          key={story._uid}
        >
          <div className="grid__col-1-3 project-page__back-button-wrapper">
            {/* https://reactrouter.com/en/main/start/concepts
                History Action - One of POP, PUSH, or REPLACE. Users can arrive at a URL for one of these three reasons. A push when a new entry is added to the history stack (typically a link click or the programmer forced a navigation). A replace is similar except it replaces the current entry on the stack instead of pushing a new one. Finally, a pop happens when the user clicks the back or forward buttons in the browser chrome. */}
            {location.action === "PUSH" ? (
              <Button isBackButton={true} to={-1} path={`previous page`} />
            ) : (
              <Button
                isBackButton={true}
                to={`/projects-showcase/`}
                path={`projects showcase`}
              />
            )}
          </div>

          <div className="grid__col-1-6 project-page__hero">
            <h1 className="default-template__title">{story.content.title}</h1>
            {story.content.subtitle ? (
              <p className="default-template__subtitle">
                {story.content.subtitle}
              </p>
            ) : (
              <></>
            )}
          </div>

          <div className="grid__col-1-6 default-template__content project-page__content">
            <HeroImage
              imageFilename={story.content.mainImage.filename}
              imageALT={story.content.mainImage.alt}
              imageSource={story.content.mainImage.source}
              imageCopyright={story.content.mainImage.copyright}
              categoryType={story.content.categoryType}
              categoryLocation={story.content.categoryLocation}
              categoryFocus={story.content.categoryFocus}
              collaborationBadge={story.content.collaboration}
              // imageCaption={story.content.mainImage.title}
            />

            <section
              className={`project-page__meta ${
                story.content.collaboration
                  ? "project-page__meta--seeking-collaboration"
                  : ""
              }`}
            >
              <div className="project-page__meta-details">
                <b>Project Dates:</b>
                <time>
                  <span>{getDate(story.content.startDate)}</span> <span>-</span>
                  {story.content.completedDate ? (
                    <span>{getDate(story.content.completedDate)}</span>
                  ) : (
                    <>
                      <span>Ongoing</span>
                    </>
                  )}
                </time>
              </div>
              <div className="project-page__meta-details">
                <b>Collaborators:</b>
                <ul className="project-page__meta-list">
                  {story.content.collaborators.map(item => (
                    <li key={item._uid}>
                      {item.linkText ? (
                        <span>
                          {item.linkURL.linktype === "url" ? (
                            <Link
                              linkType="--external"
                              text={item.linkText}
                              to={item.linkURL.url}
                            />
                          ) : (
                            <Link
                              linkType="--internal"
                              text={item.linkText}
                              to={`/${item.linkURL.cached_url}`}
                            />
                          )}
                        </span>
                      ) : (
                        <span>{item.text}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {story.content.partnerships.length ? (
                <div className="project-page__meta-details">
                  <b>In partnership with:</b>
                  <ul className="project-page__meta-list">
                    {story.content.partnerships.map(item => (
                      <li key={item._uid}>
                        {item.linkText ? (
                          <span>
                            {item.linkURL.linktype === "url" ? (
                              <Link
                                linkType="--external"
                                text={item.linkText}
                                to={item.linkURL.url}
                              />
                            ) : (
                              <Link
                                linkType="--internal"
                                text={item.linkText}
                                to={`/${item.linkURL.cached_url}`}
                              />
                            )}
                          </span>
                        ) : (
                          <span>{item.text}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <></>
              )}

              {story.content.fundedBy.length ? (
                <div className="project-page__meta-details">
                  <b>Project funded by:</b>
                  <ul className="project-page__meta-list">
                    {story.content.fundedBy.map(item => (
                      <li key={item._uid}>
                        {item.linkText ? (
                          <span>
                            {item.linkURL.linktype === "url" ? (
                              <Link
                                linkType="--external"
                                text={item.linkText}
                                to={item.linkURL.url}
                              />
                            ) : (
                              <Link
                                linkType="--internal"
                                text={item.linkText}
                                to={`/${item.linkURL.cached_url}`}
                              />
                            )}
                          </span>
                        ) : (
                          <span>{item.text}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <></>
              )}
            </section>

            {JSON.stringify(story.content.firstSection).length ? (
              <section
                className={`project-page__first-section ${
                  JSON.stringify(story.content.firstSection).length >= 900
                    ? "project-page__first-section--two-columns"
                    : ""
                }`}
              >
                <RichText text={story.content.firstSection} />
              </section>
            ) : (
              <></>
            )}
          </div>

          {story.content.sections.map(section => (
            <section
              key={section._uid}
              className="grid__col-1-6 default-template__content project-page__content project-page__section"
            >
              <div
                className={`project-page__section-layout project-page__section-layout--${section.component}`}
              >
                {section.component === "textAndFigureOnRight" ? (
                  <>
                    {/* Text */}
                    {section.text ? (
                      <>
                        <RichText text={section.text} />
                      </>
                    ) : (
                      <></>
                    )}
                    {/* /Text */}

                    {/* Figures */}
                    {section.figures ? (
                      <>
                        {section.figures.map(figure => (
                          <div
                            key={figure._uid}
                            className="project-page__figure-wrapper"
                          >
                            <Figure
                              isVideo={figure.figure.is_external_url}
                              filename={`${figure.figure.filename}/m/${
                                section.component === "oneFigure"
                                  ? "1600x900"
                                  : "800x450"
                              }`}
                              imageCopyright={figure.figure.copyright}
                              imageSource={figure.figure.source}
                              imageALT={figure.figure.alt}
                              caption={figure.figureCaption}
                            />
                          </div>
                        ))}
                      </>
                    ) : (
                      <></>
                    )}
                    {/* /Figures */}
                  </>
                ) : (
                  <>
                    {/* Figures */}
                    {section.figures ? (
                      <>
                        {section.figures.map(figure => (
                          <div
                            key={figure._uid}
                            className="project-page__figure-wrapper"
                          >
                            <Figure
                              isVideo={figure.figure.is_external_url}
                              filename={figure.figure.filename}
                              imageCopyright={figure.figure.copyright}
                              imageSource={figure.figure.source}
                              imageALT={figure.figure.alt}
                              caption={figure.figureCaption}
                            />
                          </div>
                        ))}
                      </>
                    ) : (
                      <></>
                    )}
                    {/* /Figures */}

                    {/* Text */}
                    {section.text ? (
                      <>
                        <RichText text={section.text} />
                      </>
                    ) : (
                      <></>
                    )}
                    {/* /Text */}
                  </>
                )}
              </div>
            </section>
          ))}

          {story.content.furtherReading ? (
            <>
              {story.content.furtherReading.length === 0 ? (
                <></>
              ) : (
                <section
                  className="
                  grid__col-1-6
                  default-template__content
                  project-page__related-links"
                >
                  <h2>Related Links:</h2>

                  <ul>
                    {story.content.furtherReading.map(link => (
                      <li key={link._uid}>
                        <Link
                          text={link.linkText}
                          to={link.linkURL.url}
                          linkType="--external"
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          ) : (
            <></>
          )}
        </div>
      </Base>
    </>
  )
}

export const Head = ({ pageContext, location }) => {
  let story = pageContext.story
  story = useStoryblokState(story, location)

  let seoTitle = `${story.content.title} | BIO-FUTURES for Transplanetary Habitats`
  let seoDescription = `${
    story.content.title
  } is a project by${story.content.collaborators.map(item =>
    item.linkText ? ` ${item.linkText}` : ` ${item.text}`
  )}. This project explores ${story.content.categoryType}, ${
    story.content.categoryLocation
  } and ${story.content.categoryFocus}. ${
    story.content.collaboration
      ? "This project is an ongoing project and is currently seeking collaboration."
      : "Keep reading to learn more."
  } 
   `
  let seoImage = `${story.content.mainImage.filename}`
  let seoImageAlt = `${story.content.mainImage.alt}`
  let seoUrl = `${location.pathname}`
  let seoAuthors = [
    story.content.collaborators.map(item =>
      item.linkText ? `${item.linkText}` : `${item.text}`
    ),
  ]
  let seoTags = [
    `${story.content.categoryType}`,
    `${story.content.categoryLocation}`,
    `${story.content.categoryFocus}`,
    `bio`,
    `futures`,
    `design`,
    `space`,
    `architecture`,
    `research`,
  ]

  return (
    <Seo
      title={seoTitle} // defaults to siteMetadata set in Gatsby config
      description={seoDescription} // defaults to siteMetadata set in Gatsby config
      image={seoImage}
      imageAltText={seoImageAlt}
      pathname={seoUrl}
    >
      <meta property="og:type" content="article" />
      <meta property="og:article:author" content={seoAuthors} />
      <meta property="og:article:tag" content={seoTags} />
    </Seo>
  )
}

export default ProjectPage
